import React, { useState, useRef, useEffect, useContext } from 'react';
import './PropertyCard.css';
import { toast } from 'react-toastify';
import { MyContext } from '../../../../../Context/MyContext';
import { propertyActivityLabel } from '../../../../../utils/userActivity';
import { useNavigate } from 'react-router-dom';
import ImageWithLoader from "../../../../../Config/ImageWithLoader";
import { buildPropertyWebShareUrl, warmPropertyShareCover } from "../../../../utils/propertyShareUrl";
import { formatStateInCity } from '../../../../../utils/locationFormat';
import { isCustomDropdownValue, decodeCustomDropdownValue } from '../../../../../utils/dropdownCustomValue';
import { isSoldOut } from '../../../../../utils/propertyStatus';
import AppIcon from '../../../../../Components/AppIcon';
import dtcpBadge from '../../../../../Assets/dtcp.png';

const resolveLabel = (dropdown, dropdown_id, value) => {
  if (value === null || value === undefined || value === '') return null;
  if (isCustomDropdownValue(value)) return decodeCustomDropdownValue(value);
  const dd = dropdown?.find(d => d.id === dropdown_id);
  if (!dd) return null;
  const opt = dd.options?.find(o => String(o.value) === String(value));
  return opt?.label || null;
};

// ── Ordinal suffix: 1→1st, 2→2nd, 3→3rd, 4→4th … ──
const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const SOCIALS = [
  {
    id: 'whatsapp', label: 'WhatsApp', color: '#25D366',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>,
    getUrl: (t, u) => `https://wa.me/?text=${encodeURIComponent(`${u}\n\n${t}`)}`,
  },
  {
    id: 'facebook', label: 'Facebook', color: '#1877F2',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>,
    getUrl: (t, u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}`,
  },
  {
    id: 'copy', label: 'Copy Link', color: '#555',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>,
    getUrl: null,
  },
];

export const SharePopup = ({ open, onClose, shareText, shareUrl, propertyId }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Pre-build OG cover so the first WhatsApp/Telegram paste shows the image.
    const idFromUrl = String(shareUrl || '').match(/\/(?:listing|property)\/(\d+)/)?.[1];
    warmPropertyShareCover(propertyId || idFromUrl);
  }, [open, propertyId, shareUrl]);

  if (!open) return null;
  const handleSocial = (s) => {
    if (s.id === 'copy') {
      navigator.clipboard.writeText(shareUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
      return;
    }
    window.open(s.getUrl(shareText, shareUrl), '_blank', 'noopener');
    onClose();
  };
  return (
    <div className="share-overlay" onClick={onClose}>
      <div className="share-sheet" onClick={e => e.stopPropagation()}>
        <div className="share-handle" />
        <p className="share-title">Share Property</p>
        <div className="share-grid">
          {SOCIALS.map(s => (
            <button key={s.id} className="share-btn" onClick={() => handleSocial(s)}>
              <span className="share-icon" style={{ background: s.color + '18', color: s.color }}>{s.icon}</span>
              <span className="share-label">
                {s.id === 'copy' && copied ? (
                  <><AppIcon name="check" size={14} /> Copied!</>
                ) : s.label}
              </span>
            </button>
          ))}
        </div>
        <button className="share-cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
};

// ── Format price ──
const fmtPrice = (p) => {
  if (!p && p !== 0) return null;
  if (p >= 10000000) return `₹${(p / 10000000).toFixed(2)} Cr`;
  if (p >= 100000) return `₹${(p / 100000).toFixed(2)} L`;
  return `₹${p.toLocaleString('en-IN')}`;
};

// ── Get unit label — strip hyphens: "sq-ft" → "sqft" ──
const getUnitLabel = (unitId, landtype) => {
  if (!unitId || !landtype?.length) return 'sqft';
  const lt = landtype.find(l => Number(l.id) === Number(unitId));
  if (!lt) return 'sqft';
  return (lt.landtype_name || 'sqft').replace(/-/g, '');
};

// ── Convert area to sqft for price/sqft ──
const toSqft = (value, unitId, landtype) => {
  if (!value) return null;
  if (!unitId || !landtype?.length) return value;
  const lt = landtype.find(l => Number(l.id) === Number(unitId));
  if (!lt?.calculation?.to_sqft) return value;
  return value * lt.calculation.to_sqft;
};

/* ── Main Card ── */
const PropertyCard = ({ property, badgeLabel, badgeColor }) => {
  const {
    categorys, cities, states, aminities, propertydetail,
    dropdown, wishlist, toggleWishlist, imgUrl, landtype
  } = useContext(MyContext);

  const {
    thumbnail, slider, slider_show, slider_work,
    category_id, city_id, state_id, price,
    is_dtcp,
    bhk, direction, amenities_id, property_availability,
    buildup_area, plot_area, land_area,
    property_floor_no, total_floors,
    extra_fields,
  } = property;

  const ef = extra_fields || {};
  const ea1 = ef.land_area1 ? Number(ef.land_area1) : null;
  const ea1Unit = ef.land_area1_unit ? Number(ef.land_area1_unit) : null;
  const buUnit = ef.buildup_area_unit ? Number(ef.buildup_area_unit) : null;

  const resolveImg = (path) => imgUrl ? imgUrl(path) : path;
  const canShowSliderImages = Boolean(slider_show);
  const rawImages = canShowSliderImages
    ? [thumbnail, ...(slider || [])].filter(Boolean)
    : (thumbnail ? [thumbnail] : []);
  const uniqueImages = [...new Set(rawImages)];
  const images = uniqueImages.map(resolveImg).filter(Boolean);
  if (!images.length) images.push('/placeholder.jpg');

  const [current, setCurrent] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const dragStartX = useRef(0);
  const autoRef = useRef(null);
 const isWished = wishlist.map(String).includes(String(property.id));
  const navigate = useNavigate();

  const prev = () => setCurrent(i => (i - 1 + images.length) % images.length);
  const next = () => setCurrent(i => (i + 1) % images.length);

  useEffect(() => {
    clearInterval(autoRef.current);
    if (slider_work && canShowSliderImages && images.length > 1) autoRef.current = setInterval(next, 3000);
    return () => clearInterval(autoRef.current);
  }, [slider_work, canShowSliderImages, images.length]);

  const onMouseDown = (e) => { dragStartX.current = e.clientX; };
  const onMouseUp = (e) => { const d = dragStartX.current - e.clientX; if (d > 40) next(); else if (d < -40) prev(); };
  const onTouchStart = (e) => { dragStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => { const d = dragStartX.current - e.changedTouches[0].clientX; if (d > 40) next(); else if (d < -40) prev(); };

  // ── Dropdown labels ──
  const bhkLabel = ef.bhk_custom || resolveLabel(dropdown, 5, bhk) || (bhk ? `${bhk} BHK` : null);
  const dirLabel = resolveLabel(dropdown, 1, direction);
  const availLabel = resolveLabel(dropdown, 8, property_availability);

  // ── Category / City / State ──
  const category = categorys?.find(c => c.id === category_id);
  const city = cities?.find(c => c.id === city_id);
  const state = states?.find(s => s.id === state_id);
  const locationStr = formatStateInCity(state, city);

  // ── Area display ──
  // Prefer admin card_land_show config when present for this category; else legacy priority
  const areaDisplay = (() => {
    const landFrom = ef.land_area_cent_from ? Number(ef.land_area_cent_from) : null;
    const landTo = ef.land_area_cent_to ? Number(ef.land_area_cent_to) : null;
    const catId = Number(category_id);

    const cardLandEnabled = (fieldKey) => {
      const cfg = (propertydetail || []).find((d) => d.field === fieldKey);
      if (!cfg || !Array.isArray(cfg.card_land_show) || !cfg.card_land_show.length) return null; // null = not configured
      return cfg.card_land_show.map(Number).includes(catId);
    };

    const anyCardLandConfigured = (propertydetail || []).some(
      (d) => Array.isArray(d.card_land_show) && d.card_land_show.length > 0
    );

    // Project cent range
    if (property.is_project && landFrom && landTo) {
      const enabled = cardLandEnabled('land_area') ?? cardLandEnabled('land_area_cent');
      if (enabled === true || (enabled === null && !anyCardLandConfigured)) {
        const unitId = ef.land_area_cent_unit ? Number(ef.land_area_cent_unit) : 4;
        const unit = getUnitLabel(unitId, landtype) || 'cent';
        return { isRange: true, from: landFrom, to: landTo, unit, unitId };
      }
    }

    // Config-driven: first card_land_show-enabled field with a value
    if (anyCardLandConfigured) {
      const configured = (propertydetail || []).find((d) => {
        if (!Array.isArray(d.card_land_show) || !d.card_land_show.map(Number).includes(catId)) return false;
        if (d.field === 'plot_size') return false;
        if (d.field === 'land_area_cent') {
          return property.land_area_cent != null && Number(property.land_area_cent) !== 0;
        }
        if (d.field === 'land_area') {
          return (property.land_area != null && Number(property.land_area) !== 0)
            || (ef.land_area1 != null && Number(ef.land_area1) !== 0)
            || (property.land_area_cent != null && Number(property.land_area_cent) !== 0);
        }
        const val = property[d.field] ?? ef[d.field];
        return val != null && Number(val) !== 0;
      });

      if (!configured) return null;

      if (configured.field === 'land_area_cent') {
        const val = Number(property.land_area_cent);
        const unitId = ef.land_area_cent_unit ? Number(ef.land_area_cent_unit) : 4;
        const unit = getUnitLabel(unitId, landtype) || 'Cent';
        return { isRange: false, raw: val, unit, unitId };
      }
      if (configured.field === 'land_area' && !(property.land_area > 0) && ea1 > 0) {
        const unit = getUnitLabel(ea1Unit, landtype);
        return { sqftVal: toSqft(ea1, ea1Unit, landtype), raw: ea1, unit, unitId: ea1Unit };
      }
      const rawVal = Number(property[configured.field] ?? ef[configured.field]);
      const unitId = ef[`${configured.field}_unit`] ? Number(ef[`${configured.field}_unit`]) : null;
      const unit = getUnitLabel(unitId, landtype) || (configured.unit || 'sqft').replace(/-/g, '');
      return { sqftVal: toSqft(rawVal, unitId, landtype) || rawVal, raw: rawVal, unit, unitId };
    }

    // Legacy fallback
    if (buildup_area > 0) {
      const u = buUnit || null;
      const unit = getUnitLabel(u, landtype);
      return { sqftVal: toSqft(buildup_area, u, landtype), raw: buildup_area, unit, unitId: u };
    }
    if (plot_area > 0) {
      const u = ef.plot_area_unit ? Number(ef.plot_area_unit) : null;
      const unit = getUnitLabel(u, landtype);
      return { sqftVal: toSqft(plot_area, u, landtype) || plot_area, raw: plot_area, unit, unitId: u };
    }
    if (land_area > 0) {
      const u = ef.land_area_unit || ef.land_area1_unit || ef.land_area_cent_unit;
      const unitId = u ? Number(u) : null;
      const unit = getUnitLabel(unitId, landtype);
      return { sqftVal: toSqft(land_area, unitId, landtype) || land_area, raw: land_area, unit, unitId };
    }
    if (ea1 > 0) {
      const unit = getUnitLabel(ea1Unit, landtype);
      return { sqftVal: toSqft(ea1, ea1Unit, landtype), raw: ea1, unit, unitId: ea1Unit };
    }
    return null;
  })();

  // Area display text: "21 sqft"
const areaText = (() => {
  if (!areaDisplay) return null;

  // ✅ Range display (for projects)
  if (areaDisplay.isRange) {
    return `${areaDisplay.from} - ${areaDisplay.to} ${areaDisplay.unit}`;
  }

  // ✅ Normal display
  return `${Number(areaDisplay.raw).toLocaleString('en-IN')} ${areaDisplay.unit}`;
})();

  // Price per sqft
const pricePerSqft = (() => {
  // ❌ Skip for range
  if (areaDisplay?.isRange) return null;

  if (!price || !areaDisplay?.sqftVal) return null;
  const perSqft = Math.round(price / areaDisplay.sqftVal);
  return `₹${perSqft.toLocaleString('en-IN')}/sqft`;
})();

  // ── Floor display: ordinal with superscript ──
  // property_floor_no: 2 → "2ⁿᵈ Floor"
  const floorDisplay = (() => {
    if (!property_floor_no || property_floor_no <= 0) return null;
    const n = Number(property_floor_no);
    const v = n % 100;
    const sfx = (['th', 'st', 'nd', 'rd'])[(v - 20) % 10] || (['th', 'st', 'nd', 'rd'])[v] || 'th';
    return { num: n, suffix: sfx };
  })();

  // ── Amenities ──
  const MAX_AM = 5;
  const amNames = (amenities_id || [])
    .map(id => aminities?.find(a => a.id === id)?.aminitie_name)
    .filter(Boolean);
  const amVisible = amNames.slice(0, MAX_AM);
  const amExtra = amNames.length - MAX_AM;

const priceStr = (() => {
  if (price) return fmtPrice(price);

  if (property.price_from && property.price_to) {
    return `${fmtPrice(property.price_from)} - ${fmtPrice(property.price_to)}`;
  }

  if (property.price_from) {
    return `From ${fmtPrice(property.price_from)}`;
  }

  if (property.price_to) {
    return `Up to ${fmtPrice(property.price_to)}`;
  }

  return null;
})();
  const shareText = `${category?.category_name || 'Property'} — ${priceStr || ''} | ${locationStr || ''}`;
  const shareUrl = buildPropertyWebShareUrl({
    propertyId: property.id,
    categoryName: category?.category_name,
    cityName: city?.city_name,
  });

  const soldOut = isSoldOut(property);


const getListingLabel = (type) => {
  if (!type) return null;

  const t = type.toLowerCase();

  if (t === 'sale') return 'For Sale';
  if (t === 'rent') return 'For Rent';

  return type;
};

const getListingColor = (type, fallback) => {
  if (!type) return fallback;

  const t = type.toLowerCase();

  if (t === 'sale') return '#2563eb'; // blue
  if (t === 'rent') return '#16a34a'; // green

  return fallback;
};


  return (
    <>
      <div
        className={`pcard${soldOut ? ' pcard--sold-out' : ''}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => navigate(`/property/${property.id}`)}
      >
        {/* ── Image ── */}
        <div className={`pcard-img-outer${soldOut ? ' pcard-img-outer--sold-out' : ''}`}>
          <div
            className="pcard-img-clip"
            onMouseDown={onMouseDown} onMouseUp={onMouseUp}
            onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
          >
            <ImageWithLoader src={images[current]} alt="property" className="pcard-img" draggable={false} />

          <span
  className="pcard-badge"
  style={{ backgroundColor: getListingColor(property?.listing_type, badgeColor) }}
>
  {getListingLabel(property.listing_type)}
</span>
            {is_dtcp && (
              <img src={dtcpBadge} alt="DTCP Approved" className="pcard-dtcp-badge" />
            )}
            {/* {soldOut && (
              <div className="pcard-sold-seal" aria-label="Sold out">
                <div className="pcard-sold-seal__ring">
                  <span className="pcard-sold-seal__label">SOLD</span>
                  <span className="pcard-sold-seal__sub">OUT</span>
                </div>
              </div>
            )} */}

            {/* Share */}
            <button className="pcard-share" onClick={e => { e.stopPropagation(); setShareOpen(true); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
            </button>

            {/* Wishlist */}
            <button
              className={`pcard-heart ${isWished ? 'pcard-heart-active' : ''}`}
              onClick={e => {
                e.stopPropagation();
                const ok = toggleWishlist(property.id, {
                  label: propertyActivityLabel(property, 'Added to wishlist'),
                  url: `/properties/${property.id}`,
                });
                if (!ok) { navigate('/login'); return; }
                isWished ? toast.info('Removed from Wishlist') : toast.success('Added to Wishlist');
              }}
            >
              <svg viewBox="0 0 24 24" fill={isWished ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" width="15" height="15">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </button>

            {/* Dots */}
            {images.length > 1 && (
              <div className="pcard-img-footer">
                <div className="pcard-dots">
                  {images.map((_, i) => (
                    <span key={i} className={`pcard-dot ${i === current ? 'active' : ''}`}
                      onClick={e => { e.stopPropagation(); setCurrent(i); }} />
                  ))}
                </div>
              </div>
            )}

            {/* Amenities hover overlay */}
            {hovered && amNames.length > 0 && (
              <div className="pcard-am-overlay" onClick={e => e.stopPropagation()}>
                <div className="pcard-am-list">
                  {amVisible.map((n, i) => (
                    <span key={i} className="pcard-am-chip">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="8" height="8">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {n}
                    </span>
                  ))}
                  {amExtra > 0 && <span className="pcard-am-chip pcard-am-more">+{amExtra} more</span>}
                </div>
              </div>
            )}
          </div>

          {/* Arrows */}
          {images.length > 1 && (
            <>
              <button className={`pcard-arrow pcard-arrow-left ${hovered ? 'arrow-show' : ''}`}
                onClick={e => { e.stopPropagation(); prev(); }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="17" height="17">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button className={`pcard-arrow pcard-arrow-right ${hovered ? 'arrow-show' : ''}`}
                onClick={e => { e.stopPropagation(); next(); }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="17" height="17">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </>
          )}
        </div>

        {soldOut && (
          <div className="pcard-sold-strip" role="status">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>Sold Out — View details only</span>
          </div>
        )}

        {/* ── Body ── */}
        <div className="pcard-body">

          {/* Row 1: Category pill · State in City */}
          <div className="pcard-row1">
            {category?.category_name && (
              <span className="pcard-cat-pill">{category.category_name}</span>
            )}
            {locationStr && (
              <span className="pcard-loc-text">{locationStr}</span>
            )}
          </div>

          {/* Row 2: Price + per/sqft (left)  ·  Area + Floor (right) */}
          {(priceStr || areaText || floorDisplay) && (
           <div className="pcard-row2">

  {/* LEFT: Price */}
  <div className="pcard-col-left">
    {priceStr && <span className="pcard-price">{priceStr}</span>}
    {pricePerSqft && (
      <span className="pcard-per-sqft">{pricePerSqft}</span>
    )}
  </div>

  {/* CENTER: BHK */}
  {bhkLabel && (
    <div className="pcard-col-center">
      <span className="pcard-bhk-inline">{bhkLabel}</span>
    </div>
  )}

  {/* RIGHT: Area */}
  <div className="pcard-col-right">
    {areaText && (
      <span className="pcard-area-val">{areaText}</span>
    )}
    {floorDisplay && (
      <span className="pcard-floor-val">
        {floorDisplay.num}<sup>{floorDisplay.suffix}</sup> Floor
      </span>
    )}
  </div>

</div>
          )}

          {/* Row 3 footer: Direction · BHK · Availability */}
          {(dirLabel || bhkLabel || availLabel) && (
            <div className="pcard-row3">
              {dirLabel && (
                <span className="pcard-tag pcard-tag-dir">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11">
                    <polygon points="3 11 22 2 13 21 11 13 3 11" />
                  </svg>
                  {dirLabel} Facing
                </span>
              )}
              {/* {bhkLabel && (
                <span className="pcard-tag pcard-tag-bhk">{bhkLabel}</span>
              )} */}
              {availLabel && (
                <span className={`pcard-tag pcard-tag-avail pcard-avail-${availLabel?.toLowerCase().replace(/\s+/g, '-')}`}>
                  {availLabel}
                </span>
              )}
            </div>
          )}

        </div>
      </div>

      <SharePopup
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        shareText={shareText}
        shareUrl={shareUrl}
        propertyId={property?.id}
      />
    </>
  );
};

export default PropertyCard;